import { size } from 'lodash'
import { Controller, FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { FormFieldset, PriceInput, Select, Switch, Textarea } from 'shared/components'
import { FormError } from 'shared/atoms'
import { InputGroup } from 'shared/molecules'
import { usePayoutMethodLabels, useSelect, useUserRoles } from 'shared/hooks'
import type { CurrenciecRes } from 'shared/types/general.types'
import { payoutMethodKinds } from 'shared/types/payout.types'
import type { PayoutMethodKind } from 'shared/types/payout.types'
import { DEFAULT_MAX_LETTER_COUNT } from '../../config/defaults'
import { toOptions } from '../../utils/options'

const payoutMethodOptionClassName =
	'flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card p-2.5 transition-colors hover:bg-muted/50 [&:has(:checked)]:border-primary [&:has(:checked)]:bg-primary-soft'

type PaymentsFieldsProps = {
	form: UseFormReturn<FieldValues>
	currencies: CurrenciecRes | undefined
	isCurrenciesLoading: boolean
}

export const PaymentsFields = ({ form, currencies, isCurrenciesLoading }: PaymentsFieldsProps) => {
	const { t } = useTranslation()
	const { isSuperuser } = useUserRoles()
	const payoutMethodLabels = usePayoutMethodLabels()
	const {
		register,
		control,
		setValue,
		trigger,
		watch,
		formState: { errors }
	} = form
	const { handleSelectChange } = useSelect({ setValue, trigger })
	const fieldErrors = errors as any

	const sendgridTemplates = [
		{ name: 'sendgrid_receipt_template_id', label: t('Sendgrid receipt template ID') },
		{ name: 'sendgrid_end_reminder_template_id', label: t('Sendgrid end reminder template ID') },
		{
			name: 'sendgrid_start_reminder_template_id',
			label: t('Sendgrid start reminder template ID')
		},
		{ name: 'sendgrid_welcome_template_id', label: t('Sendgrid welcome template ID') },
		{
			name: 'sendgrid_payout_method_reminder_template_id',
			label: t('Sendgrid payout method missing template ID')
		}
	]

	const smsTemplates = [
		{ name: 'start_reminder_sms_template', label: t('Start reminder SMS template') },
		{ name: 'end_reminder_sms_template', label: t('End reminder SMS template') }
	]

	return (
		<>
			<InputGroup
				id="payment_receipt_url"
				type="url"
				label={t('Payment receipt url')}
				{...register('payment_receipt_url')}
			/>
			<InputGroup
				id="payment_cancel_url"
				type="url"
				label={t('Payment cancel url')}
				{...register('payment_cancel_url')}
			/>
			<Switch label={t('Coupons enabled')} {...register('coupons_enabled')} />

			<FormFieldset title={t('Payout methods')}>
				<p className="-mt-2 text-[12px] text-muted-foreground">
					{t('Methods consignors can be paid with.')}
				</p>
				<Controller
					name="enabled_payout_methods"
					control={control}
					render={({ field }) => {
						const enabled: PayoutMethodKind[] = Array.isArray(field.value) ? field.value : []
						const toggle = (kind: PayoutMethodKind) =>
							field.onChange(
								payoutMethodKinds.filter((item) =>
									item === kind ? !enabled.includes(kind) : enabled.includes(item)
								)
							)

						return (
							<div className="grid gap-2 sm:grid-cols-2">
								{payoutMethodKinds.map((kind) => (
									<label key={kind} className={payoutMethodOptionClassName}>
										<input
											type="checkbox"
											className="h-4 w-4 shrink-0 accent-primary"
											checked={enabled.includes(kind)}
											onChange={() => toggle(kind)}
											onBlur={field.onBlur}
										/>
										<span className="text-[13px] font-semibold text-foreground">
											{payoutMethodLabels[kind]}
										</span>
									</label>
								))}
							</div>
						)
					}}
				/>
				<FormError text={fieldErrors.enabled_payout_methods?.message} />
			</FormFieldset>

			<FormFieldset title="Rapyd">
				<InputGroup
					id="rapyd_access_key"
					type="password"
					label={t('Rapyd access key')}
					{...register('rapyd_access_key')}
				/>
				<InputGroup
					id="rapyd_secret_key"
					type="password"
					label={t('Rapyd secret key')}
					{...register('rapyd_secret_key')}
				/>
				<Switch label={t('Rapyd sandbox')} {...register('rapyd_sandbox')} />
				<Controller
					name="currency"
					control={control}
					render={({ field }) => (
						<Select
							id="currency"
							variant="form"
							isClearable={false}
							label={t('Currency')}
							options={toOptions(currencies)}
							{...field}
							isLoading={isCurrenciesLoading}
							error={fieldErrors.currency?.value}
							onChange={(data: any) =>
								handleSelectChange({ id: 'currency', value: data?.value, label: data?.label })
							}
						/>
					)}
				/>
			</FormFieldset>

			{isSuperuser && (
				<>
					<FormFieldset title="Resalex">
						<InputGroup
							id="resalex_client_id"
							label={t('Resalex App ID')}
							{...register('resalex_client_id')}
						/>
						<InputGroup
							id="resalex_client_secret"
							type="password"
							label={t('Resalex secret key')}
							{...register('resalex_client_secret')}
						/>
						<Switch label={t('Resalex sandbox')} {...register('resalex_sandbox')} />
					</FormFieldset>

					<FormFieldset title="Netgíró">
						<InputGroup
							id="netgiro_app_id"
							label={t('Netgíró App ID')}
							{...register('netgiro_app_id')}
						/>
						<InputGroup
							id="netgiro_secret_key"
							type="password"
							label={t('Netgíró secret key')}
							{...register('netgiro_secret_key')}
						/>
						<Switch label={t('Netgíró sandbox')} {...register('netgiro_sandbox')} />
					</FormFieldset>

					<FormFieldset title={t('Sendgrid template')}>
						{sendgridTemplates.map(({ name, label }) => (
							<InputGroup key={name} id={name} label={label} {...register(name)} />
						))}
					</FormFieldset>

					<FormFieldset title={t('SMS template')}>
						{smsTemplates.map(({ name, label }) => (
							<Textarea
								key={name}
								id={name}
								rows={2}
								label={label}
								required
								letters={size(watch(name))}
								maxLetterCount={DEFAULT_MAX_LETTER_COUNT}
								{...register(name)}
								error={fieldErrors[name]}
							/>
						))}
					</FormFieldset>

					<FormFieldset title={t('Fee')} className="sm:grid-cols-2">
						<PriceInput
							id="booth_takedown_fee"
							label={t('Area takedown fee')}
							{...register('booking_fees.booth_takedown_fee')}
							error={fieldErrors.booking_fees?.booth_takedown_fee}
						/>
						<PriceInput
							id="booth_takedown_fee_notified"
							label={t('Area takedown fee notified')}
							{...register('booking_fees.booth_takedown_fee_notified')}
							error={fieldErrors.booking_fees?.booth_takedown_fee_notified}
						/>
						<PriceInput
							id="product_storage_fee_per_day"
							label={t('Product storage fee per day')}
							{...register('booking_fees.product_storage_fee_per_day')}
							error={fieldErrors.booking_fees?.product_storage_fee_per_day}
						/>
						<InputGroup
							id="product_storage_max_days"
							label={t('Product storage max days')}
							{...register('booking_fees.product_storage_max_days')}
							error={fieldErrors.booking_fees?.product_storage_max_days}
						/>
					</FormFieldset>
				</>
			)}
		</>
	)
}
